'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import { liveMatches as seedMatches } from '@/lib/seedData';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Image from 'next/image';
import Link from 'next/link';

const POLL_INTERVAL = 20000; // 20 seconds

interface LiveMatch {
  id: string;
  tournament: string;
  teamA: string;
  teamB: string;
  teamALogo: string;
  teamBLogo: string;
  image: string;
  status: string;
  isFree: boolean;
  sport: string;
}

export default function LiveNowPage() {
  const [matches, setMatches] = useState<LiveMatch[]>(seedMatches as LiveMatch[]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMatches = useCallback(async () => {
    try {
      const res = await fetch('/api/live-matches', { cache: 'no-store' });
      if (res?.ok) {
        const data = await res?.json();
        if (Array.isArray(data) && data.length > 0) {
          setMatches(data);
        }
      }
    } catch {
      // silently keep existing data on error
    }
  }, []);

  useEffect(() => {
    fetchMatches(); // initial fetch
    intervalRef.current = setInterval(fetchMatches, POLL_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchMatches]);

  return (
    <div className="min-h-screen bg-rc-bg">
      <Navbar />
      <main className="pt-14">
        <div className="max-w-[1440px] mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-2 h-2 rounded-full bg-rc-orange live-pulse"></span>
            <h1 className="text-white text-2xl font-bold">Live Now ({matches?.length})</h1>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {matches?.map((match, i) => (
              <Link
                key={match?.id || i}
                href={`/${match?.sport}/match/${match?.id}`}
                className="bg-rc-card border border-rc-border rounded-xl overflow-hidden card-hover hover:border-rc-orange/50 transition-all"
              >
                <div className="relative h-[160px] overflow-hidden">
                  <Image
                    src={match?.image}
                    alt={`${match?.teamA} vs ${match?.teamB}`}
                    fill
                    className="object-cover"
                    unoptimized
                  />
                  <div className="absolute top-2 left-2 flex gap-1">
                    {match?.isFree && (
                      <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded">FREE</span>
                    )}
                  </div>
                  <div className="absolute bottom-2 left-2">
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-rc-orange text-white text-xs font-bold rounded">
                      <span className="w-1.5 h-1.5 rounded-full bg-white live-pulse"></span>
                      Live
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-white text-xs font-semibold mb-2 line-clamp-1">{match?.tournament}</h3>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full overflow-hidden bg-rc-border flex-shrink-0">
                        <Image src={match?.teamALogo} alt={match?.teamA} width={20} height={20} unoptimized />
                      </div>
                      <span className="text-gray-300 text-xs">{match?.teamA}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full overflow-hidden bg-rc-border flex-shrink-0">
                        <Image src={match?.teamBLogo} alt={match?.teamB} width={20} height={20} unoptimized />
                      </div>
                      <span className="text-gray-300 text-xs">{match?.teamB}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
